import { useEffect, useState } from 'react';
import type { WorkspaceDocument } from '../model/workspace-document';
import type { WorkspaceRepository } from '../services/workspace-repository';

export type SaveStatus = 'saved' | 'pending' | 'error';

const AUTOSAVE_DELAY_MS = 600;

/**
 * Guarda el documento como borrador con un pequeno retardo tras cada cambio. El estado
 * se deriva comparando el documento actual con el ultimo guardado (sin efectos extra).
 *
 * @example
 * const status = useAutosave(document, services.workspaceRepository);
 */
export function useAutosave(
  document: WorkspaceDocument,
  repository: WorkspaceRepository,
): SaveStatus {
  const [saved, setSaved] = useState<{
    readonly document: WorkspaceDocument;
    readonly failed: boolean;
  }>(() => ({ document, failed: false }));

  useEffect(() => {
    if (saved.document === document) return;
    let cancelled = false;
    const timer = setTimeout(() => {
      void repository.save(document).then((result) => {
        if (!cancelled) setSaved({ document, failed: !result.ok });
      });
    }, AUTOSAVE_DELAY_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [document, repository, saved.document]);

  if (saved.document !== document) return 'pending';
  return saved.failed ? 'error' : 'saved';
}
